import { useRef } from "react";

import { LiveAudioVisualizer } from "react-audio-visualize";

import { useElementWidth } from "../hooks/useElementWidth";
import { useTrackRecorder } from "../hooks/useTrackRecorder";

// Shared bar geometry — used everywhere a single audio source's live
// waveform is shown (the Audio Setup mic picker, and each Scribe session
// source card), so they read as the same visual language throughout the
// flow. Thicker + more rounded than the in-person combined waveform's own
// geometry (ScribeSession's WAVE_BAR_WIDTH/GAP) — these compact,
// single-source meters need bars that read clearly at a much smaller size.
export const TRACK_WAVEFORM_BAR_WIDTH = 4; // px
export const TRACK_WAVEFORM_BAR_GAP = 4; // px

type TrackWaveformProps = {
  /** The track to visualize — a live waveform tied to its own AudioContext. */
  track: MediaStreamTrack | null;
  /** Literal CSS colour for the bars (LiveAudioVisualizer takes a string, not a class). */
  color: string;
  /** Freezes the waveform's last frame instead of animating, matching the session's pause state. */
  paused?: boolean;
  className?: string;
};

/**
 * A single audio source's live waveform — the same LiveAudioVisualizer
 * (react-audio-visualize) component used for the in-person combined
 * waveform, adapted to a raw `MediaStreamTrack` via `useTrackRecorder`
 * (LiveAudioVisualizer only accepts a MediaRecorder). Used for the Audio
 * Setup step's mic-preview meter AND each telehealth Scribe session source
 * card ("You" / "Everyone on the call"), so every single-source meter in the
 * flow looks and behaves identically.
 */
export const TrackWaveform = ({
  track,
  color,
  paused = false,
  className,
}: TrackWaveformProps) => {
  const recorder = useTrackRecorder(track, paused);
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);

  return (
    <div
      ref={containerRef}
      className={className ?? "h-6 w-12 shrink-0 overflow-hidden"}
    >
      {recorder && width > 0 && (
        <LiveAudioVisualizer
          mediaRecorder={recorder}
          width={width}
          height={24}
          barWidth={TRACK_WAVEFORM_BAR_WIDTH}
          gap={TRACK_WAVEFORM_BAR_GAP}
          barColor={color}
          backgroundColor="transparent"
        />
      )}
    </div>
  );
};
